import { usernameSchema, type ChallengeableUser, type Platform } from '@gamble-chess/contracts';
import { useState, type JSX, type SubmitEvent } from 'react';

import { useTrackedPlayers } from '../api/games.ts';
import { useOpponents, useProposeWager } from '../api/stakes.ts';
import { describeAccount, describeNoAccountToChallenge } from '../format.ts';
import {
  initialStakePolicyDraft,
  parseStakePolicy,
  type StakePolicyDraft,
} from '../stakePolicyDraft.ts';
import { Alert } from '../ui/Alert.tsx';
import { Card } from '../ui/Card.tsx';
import { Field } from '../ui/Field.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import { fieldGridClass, hintClass, inputClass, submitClass } from '../ui/styles.ts';
import { StakePolicyFields } from './StakePolicyFields.tsx';

/** No registered counterparty: the opponent does not use the app. */
const OUTSIDER = '';

interface Account {
  readonly id: string;
  readonly platform: Platform;
  readonly username: string;
}

interface ProposeWagerFormProps {
  readonly accounts: readonly Account[];
}

/** The option a select shows: the one chosen while it is still offered, otherwise the first. */
function pick<T extends { readonly id: string }>(
  options: readonly T[],
  chosen: string,
): T | undefined {
  return options.find((option) => option.id === chosen) ?? options[0];
}

function ProposeWagerForm({ accounts }: ProposeWagerFormProps): JSX.Element {
  const opponents = useOpponents();
  const propose = useProposeWager();
  const [trackedPlayerId, setTrackedPlayerId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState(OUTSIDER);
  const [opponentAccountId, setOpponentAccountId] = useState('');
  const [opponent, setOpponent] = useState('');
  const [stake, setStake] = useState<StakePolicyDraft>(initialStakePolicyDraft);
  const [invalid, setInvalid] = useState<string | undefined>(undefined);

  const account = pick(accounts, trackedPlayerId);
  const counterparty: ChallengeableUser | undefined = (opponents.data ?? []).find(
    (user) => user.id === counterpartyId,
  );
  // Derived on every render, so switching your account to another platform can never leave a
  // stale choice of theirs behind to be submitted.
  const theirAccounts =
    counterparty?.accounts.filter((option) => option.platform === account?.platform) ?? [];
  const theirAccount = pick(theirAccounts, opponentAccountId);

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (account === undefined) {
      return;
    }
    const policy = parseStakePolicy(stake);
    if (!policy.ok) {
      setInvalid(policy.error);
      return;
    }
    if (counterparty === undefined) {
      const username = usernameSchema.safeParse(opponent);
      if (!username.success) {
        setInvalid(`Opponent ${username.error.issues[0]?.message ?? 'is not valid'}.`);
        return;
      }
      setInvalid(undefined);
      propose.mutate(
        {
          opponentKind: 'outsider',
          trackedPlayerId: account.id,
          opponent: username.data,
          policy: policy.policy,
        },
        {
          onSuccess: () => {
            setOpponent('');
          },
        },
      );
      return;
    }
    if (theirAccount === undefined) {
      return;
    }
    setInvalid(undefined);
    propose.mutate({
      opponentKind: 'user',
      trackedPlayerId: account.id,
      counterpartyUserId: counterparty.id,
      opponentTrackedPlayerId: theirAccount.id,
      policy: policy.policy,
    });
  }

  const error = invalid ?? (propose.isError ? propose.error.message : undefined);
  const nothingToChallenge =
    counterparty !== undefined && account !== undefined && theirAccount === undefined;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label="Propose a wager">
      <div className={fieldGridClass}>
        <Field label="Your account">
          {(id) => (
            <select
              id={id}
              value={account?.id}
              onChange={(event) => {
                setTrackedPlayerId(event.target.value);
              }}
              className={inputClass}
            >
              {accounts.map((option) => (
                <option key={option.id} value={option.id}>
                  {describeAccount(option)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Who plays it">
          {(id) => (
            <select
              id={id}
              value={counterpartyId}
              onChange={(event) => {
                setCounterpartyId(event.target.value);
                setOpponentAccountId('');
              }}
              className={inputClass}
            >
              <option value={OUTSIDER}>Someone not on Gamble Chess</option>
              {(opponents.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          )}
        </Field>
        {counterparty === undefined ? (
          <Field label="Opponent’s username">
            {(id) => (
              <input
                id={id}
                value={opponent}
                required
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => {
                  setOpponent(event.target.value);
                }}
                className={inputClass}
              />
            )}
          </Field>
        ) : theirAccounts.length === 0 ? null : (
          <Field label="Their account">
            {(id) => (
              <select
                id={id}
                value={theirAccount?.id}
                onChange={(event) => {
                  setOpponentAccountId(event.target.value);
                }}
                className={inputClass}
              >
                {theirAccounts.map((option) => (
                  <option key={option.id} value={option.id}>
                    {describeAccount(option)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
      </div>
      {nothingToChallenge ? (
        <p className={hintClass}>
          {describeNoAccountToChallenge(counterparty.displayName, account.platform)}
        </p>
      ) : null}
      <StakePolicyFields value={stake} onChange={setStake} />
      <p className={hintClass}>
        The wager is on the next game between the two accounts. A player on Gamble Chess has to
        accept it first.
      </p>
      <div>
        <button
          type="submit"
          disabled={propose.isPending || nothingToChallenge}
          className={submitClass}
        >
          {propose.isPending ? 'Proposing…' : 'Propose wager'}
        </button>
      </div>
      {error === undefined ? null : <Alert>{error}</Alert>}
    </form>
  );
}

/** One bet on the next game between two accounts. */
export function ProposeWager(): JSX.Element {
  const players = useTrackedPlayers();

  return (
    <Card title="Propose a wager" description="One bet, on the next game between two accounts.">
      <Loaded
        query={players}
        loading="Loading your accounts…"
        error="Your chess accounts could not be loaded. Please reload the page."
      >
        {(list) =>
          list.length === 0 ? (
            <p className={hintClass}>Track a chess account first to wager on its next game.</p>
          ) : (
            <ProposeWagerForm accounts={list} />
          )
        }
      </Loaded>
    </Card>
  );
}
